import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';

// POST api/records/export-exclusion: leaves records out of the t_report_data
// export and Benchmarking, or puts them back.
export const setExportExclusion = async (
  recordIds: string[],
  excluded: boolean,
  note: string | null,
  authHeaders: () => Record<string, string>
) => {
  const res = await fetch('/api/records/export-exclusion', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ recordIds, excluded, note }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to change the export exclusion');
  }
};

interface Props {
  // Open with these records; null is closed.
  recordIds: string[] | null;
  authHeaders: () => Record<string, string>;
  onClose: () => void;
  onDone: () => void | Promise<void>;
  onError: (message: string) => void;
}

const ExportExclusionDialog: React.FC<Props> = ({ recordIds, authHeaders, onClose, onDone, onError }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const count = recordIds?.length ?? 0;

  useEffect(() => {
    if (recordIds) setNote('');
  }, [recordIds]);

  const handleExclude = async () => {
    if (!recordIds) return;
    setSaving(true);
    try {
      await setExportExclusion(recordIds, true, note.trim() || null, authHeaders);
      onClose();
      await onDone();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!recordIds} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Exclude {count} record{count === 1 ? '' : 's'} from the export</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The whole record is left out of the Report Data Export and Benchmarking, including any other products on it.
          It stays in DMOTrak and in the business&apos;s own reports, and you can include it again later.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label="Reason (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 500 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleExclude} disabled={saving}>{saving ? 'Excluding...' : 'Exclude'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportExclusionDialog;
